import React, { Dispatch, JSX, ReactNode, SetStateAction, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import NameBadge from '../components/Badge/NameBadge';
import DropdownMenu from '../components/DropdownMenu';
import { Confirm, ConfirmModalType } from '../components/Modal/ConfirmModal';
import { useMenuContext } from '../context/MenuContext';
import { useStorroApi } from '../context/StorroApiContext';
import useRealm from './realm/UseRealm';
import useDesktopClientModal from './UseDesktopClientModal';
import useUser from './useUser';
import {
  BellSimpleIcon,
  CaretRightIcon,
  CurrencyCircleDollarIcon,
  FadersIcon,
  FolderIcon,
  GaugeIcon,
  GearSixIcon,
  LaptopIcon,
  LinkSimpleIcon,
  ListIcon,
  ListChecksIcon,
  ShieldCheckeredIcon,
  SignOutIcon,
  UploadSimpleIcon,
  UserIcon,
  UsersFourIcon,
  UsersIcon,
  PuzzlePieceIcon,
  InfoIcon,
} from '@phosphor-icons/react';
import useScreenSize from './UseScreenSize';
import { ScreenSize } from 'types/ScreenSize';
import useTheme from './UseTheme';
import useRealmSwitcher from './UseRealmSwitcher';
import { useTranslation } from 'react-i18next';

export interface MenuItem {
  path: string;
  title: string;
  shortName?: string;
  icon: ReactNode;
  badgeCount?: number;
}

interface MobileMenuItem extends Pick<MenuItem, 'title' | 'icon'> {
  onClick?: () => void;
  path?: string;
  className?: string;
}

export enum MenuMode {
  HIDDEN = 'hidden', // the menu is hidden
  SMALL = 'small', // this is the small mode, e.g. when we just show the icons in the left sidebar
  LARGE = 'large', // the default mode, e.g. we show the full menu
}

export enum MenuWidth {
  SMALL = 50,
  SMALL_AGENCY_DRIVE = 75,
  LARGE = 210,
}

interface ReturnType {
  isOpen: boolean | undefined;
  setIsOpen: Dispatch<SetStateAction<boolean | undefined>>;
  menuItems: MenuItem[];
  mobileMenuItems: MenuItem[];
  adminMenuItems: MenuItem[];
  accountMenuItemMobile: MobileMenuItem[];
  logoutMenuItemMobile: MobileMenuItem;
  accountMenuItemWeb: () => JSX.Element;
  menuMode: MenuMode;
  setMenuModeSmallBreakPoint: Dispatch<SetStateAction<ScreenSize | undefined>>;
  setMenuModeLargeBreakPoint: Dispatch<SetStateAction<ScreenSize | undefined>>;
  menuWidth: number;
}

function useMenu(): ReturnType {
  const { isOpen, setIsOpen, setMenuModeLargeBreakPoint, setMenuModeSmallBreakPoint, menuModeLargeBreakPoint, menuModeSmallBreakPoint } =
    useMenuContext();
  const { storroApi } = useStorroApi();
  const { realm: loadedRealm, realms, loadRealms, setRealm, setRealms } = useRealm();
  const { user, superAdminMode, setSuperAdminMode, setUser } = useUser();
  const { openModal: openDesktopClientModal, dialog: desktopClientDialog } = useDesktopClientModal();
  const navigate = useNavigate();
  const { width } = useScreenSize();
  const { isAgencyTheme } = useTheme();
  const { navigateToRealm } = useRealmSwitcher();
  const { t } = useTranslation();

  /**
   * Determine the width, also checking the theme.
   */
  const menuWidth = useMemo(() => {
    return isOpen ? MenuWidth.LARGE : isAgencyTheme ? MenuWidth.SMALL_AGENCY_DRIVE : MenuWidth.SMALL;
  }, [isAgencyTheme, isOpen]);

  /**
   * Switch to super admin mode
   */
  const enableSuperAdminMode = useCallback(() => {
    Confirm({
      title: 'Super admin mode',
      text: t('are-you-sure-you-want-to-switch-to-super-admin-mode', 'Are you sure you want to switch to super admin mode?'),
      confirmText: t('switch-to-super-admin-mode', 'Switch to super admin mode'),
      onConfirm: () => {
        storroApi.setSuperAdminMode(true);
        setSuperAdminMode(true);
        loadRealms();
        if (realms.length > 0) {
          navigateToRealm({ realm: realms[0], confirm: false });
        }
      },
    });
  }, [loadRealms, navigateToRealm, realms, setSuperAdminMode, storroApi, t]);

  const logout = useCallback(
    () =>
      Confirm({
        title: t('logout', 'Logout'),
        text: t('are-you-sure-you-want-to-logout', 'Are you sure you want to logout?'),
        type: ConfirmModalType.Danger,
        onConfirm: () => {
          setUser(undefined);
          setRealms([]);
          setRealm(undefined);
          storroApi.logout();

          // set the path to / so the user can login
          // and won't be navigated to the current url
          navigate('/');
        },
      }),
    [navigate, setRealm, setRealms, setUser, storroApi, t],
  );

  // when the realm is not loaded, e.g. because the url is not pointing to the correct realm and the user is on a different url (e.g. /account)
  // we need to return the first realm from the realm lists.
  const realm = useMemo(() => {
    if (loadedRealm) return loadedRealm;

    return realms.length > 0 ? realms[0] : undefined;
  }, [loadedRealm, realms]);

  // Menu items that we use for the left-sidebar and in the mobile-overview page
  const menuItems = useMemo<MenuItem[]>(() => {
    return [
      {
        path: `/realm/${realm?.publicKey}/projects`,
        title: t('files', 'Files'),
        icon: <FolderIcon />,
      },
      // {
      //   path: `/realm/${realm?.publicKey}/recent`,
      //   title: 'Recent',
      //   icon: <ClockCounterClockwise />,
      // },
      {
        path: `/realm/${realm?.publicKey}/quickshare`,
        title: 'QuickShare',
        icon: <LinkSimpleIcon />,
      },
      {
        path: `/realm/${realm?.publicKey}/request`,
        title: 'File requests',
        shortName: t('requests', 'Requests'),
        icon: <UploadSimpleIcon />,
      },
    ];
  }, [realm?.publicKey, t]);

  // Menu items for the mobile menu bar in the footer
  const mobileMenuItems = useMemo<MenuItem[]>(() => {
    return [
      {
        path: `/realm/${realm?.publicKey}/projects`,
        title: t('files', 'Files'),
        icon: <FolderIcon />,
      },
      {
        path: `/realm/${realm?.publicKey}/quickshare`,
        title: 'QuickShare',
        icon: <LinkSimpleIcon />,
      },
      {
        path: `/realm/${realm?.publicKey}/request`,
        title: 'File Requests',
        shortName: t('requests', 'Requests'),
        icon: <UploadSimpleIcon />,
      },
      {
        path: '/overview',
        title: t('more', 'More'),
        icon: <ListIcon />,
      },
    ];
  }, [realm?.publicKey, t]);

  const adminMenuItems = useMemo<MenuItem[]>(() => {
    const items: MenuItem[] = [
      {
        path: `/realm/${realm?.publicKey}/admin/stats`,
        title: t('statistics', 'Statistics'),
        icon: <GaugeIcon />,
      },
      {
        path: `/realm/${realm?.publicKey}/admin/settings`,
        title: t('settings', 'Settings'),
        icon: <GearSixIcon />,
      },
      {
        path: `/realm/${realm?.publicKey}/admin/integrations`,
        title: t('integrations', 'Integrations'),
        icon: <PuzzlePieceIcon />,
      },
      {
        path: `/realm/${realm?.publicKey}/admin/policies`,
        title: t('policies', 'Policies'),
        icon: <ListChecksIcon />,
      },
      {
        path: `/realm/${realm?.publicKey}/admin/users`,
        title: t('users', 'Users'),
        icon: <UsersIcon />,
      },
    ];

    // TODO: enable groups also for storro once the communication is out to the customer
    if (isAgencyTheme) {
      items.push({
        path: `/realm/${realm?.publicKey}/admin/groups`,
        title: t('groups', 'Groups'),
        icon: <UsersFourIcon />,
      });
    }

    items.push({
      path: `/realm/${realm?.publicKey}/admin/billing`,
      title: t('billing', 'Billing'),
      icon: <CurrencyCircleDollarIcon />,
    });

    if (storroApi.superAdminMode && user?.isSuperAdmin) {
      items.push({
        path: `/realm/${realm?.publicKey}/admin/console`,
        title: t('console', 'Console'),
        icon: <FadersIcon />,
      });
    }
    return items;
  }, [isAgencyTheme, realm?.publicKey, storroApi.superAdminMode, t, user?.isSuperAdmin]);

  /**
   * Menu items for the mobile view
   */
  const accountMenuItemMobile = useMemo<MobileMenuItem[]>(() => {
    return [
      {
        title: t('edit-account', 'Edit account'),
        icon: <UserIcon />,
        path: '/account/manage',
      },
      {
        title: t('devices', 'Devices'),
        icon: <LaptopIcon />,
        path: '/account/devices',
      },
      {
        title: t('security', 'Security'),
        icon: <ShieldCheckeredIcon weight='fill' />,
        path: '/account/security',
      },
      {
        title: t('notification', 'Notification'),
        icon: <BellSimpleIcon />,
        path: '/account/notifications',
      },
      {
        title: t('about', 'About'),
        icon: <InfoIcon />,
        path: '/account/about',
      },
    ];
  }, [t]);

  /**
   * The logout button should be separated on mobile
   */
  const logoutMenuItemMobile = useMemo<MobileMenuItem>(() => {
    return {
      title: t('logout', 'Logout'),
      icon: <SignOutIcon />,
      onClick: logout,
      className: 'text-red-500',
    };
  }, [logout, t]);

  /**
   * Set the menu mode based on the screen size
   * When the screen size is between md and lg, we should use the small mode
   */
  const menuMode = useMemo(() => {
    const largeBreakPoint = menuModeLargeBreakPoint ?? ScreenSize['2xl'];
    const smallBreakPoint = menuModeSmallBreakPoint ?? ScreenSize['md'];

    return width > largeBreakPoint ? MenuMode.LARGE : width > smallBreakPoint ? MenuMode.SMALL : MenuMode.HIDDEN;
  }, [menuModeLargeBreakPoint, menuModeSmallBreakPoint, width]);

  /**
   * Ensure the menu is open/closed based on the menu mode
   */
  useEffect(() => {
    // check also the local storage if the user has set the menu
    // to open we can open it if possible on large screens
    const currentLocalState = localStorage.getItem('leftMenuState');
    if (currentLocalState === 'open') {
      setIsOpen(true);
    }
  }, [menuMode, setIsOpen]);

  /**
   * This return an Element with a Badge and name of the user
   * If the user click on this element we show a dropDown menu
   */
  const accountMenuItemWeb = useCallback(() => {
    return (
      <>
        {storroApi.keys && (
          <DropdownMenu
            buttonClassName='w-full'
            menuPlacement='right'
            menuItems={[
              [
                {
                  element: t('account', 'Account'),
                  isHeader: true,
                },
                {
                  isVisible: !superAdminMode && user?.isSuperAdmin === true,
                  element: <>{t('super-admin-mode', 'Super admin mode')}</>,
                  onClick: () => {
                    enableSuperAdminMode();
                  },
                },
                {
                  element: <>{t('edit-account', 'Edit account')}</>,
                  onClick: () => {
                    navigate('/account/personal');
                  },
                },
                {
                  element: <>{t('devices', 'Devices')}</>,
                  onClick: () => {
                    navigate('/account/devices');
                  },
                },
                {
                  element: <>{t('desktop-client', 'Desktop client')}</>,
                  onClick: openDesktopClientModal,
                },
                {
                  element: <>{t('create-realm', 'Create Realm')}</>,
                  onClick: () => {
                    navigate('/realm/create');
                  },
                },
              ],
              [
                {
                  element: <span className='text-danger-700'>{t('logout', 'Logout')}</span>,
                  onClick: logout,
                },
              ],
            ]}
          >
            <a className='flex items-center gap-x-2' href='#' onClick={event => event.preventDefault()}>
              <NameBadge
                size={isOpen ? 'base' : 'sm'}
                userPublicKeyBase64={storroApi.keys.userPublicKey.toBase64()}
                firstName={user?.firstName}
                lastName={user?.lastName}
              />
              {isOpen && (
                <div className='text-sm text-left truncate'>
                  <p title={`${user?.firstName} ${user?.lastName}`}>
                    {user?.firstName} {user?.lastName}
                  </p>
                  <p className='text-xs'>{user?.email}</p>
                </div>
              )}
              <CaretRightIcon />
            </a>
          </DropdownMenu>
        )}

        {desktopClientDialog}
      </>
    );
  }, [
    desktopClientDialog,
    enableSuperAdminMode,
    isOpen,
    logout,
    navigate,
    openDesktopClientModal,
    storroApi.keys,
    superAdminMode,
    t,
    user?.email,
    user?.firstName,
    user?.isSuperAdmin,
    user?.lastName,
  ]);

  return {
    isOpen,
    setIsOpen,
    menuItems,
    mobileMenuItems,
    adminMenuItems,
    accountMenuItemWeb,
    accountMenuItemMobile,
    logoutMenuItemMobile,
    menuMode,
    setMenuModeLargeBreakPoint,
    setMenuModeSmallBreakPoint,
    menuWidth,
  };
}

export default useMenu;
